// src/pages/AmazonFixedCostPage.tsx
//
// Amazon 固定費入力ページ。楽天版 (FiscalRakutenExpensePage) と同じ操作感
// （12ヶ月テーブル・科目行・右上保存・未保存ヒント）で、項目は Amazon 仕様:
//   - Amazon出品プラン（大口/小口。選択で月額を自動設定）→ shop_plan / shop_fee
//   - 有料システム①〜⑤（名称自由 + 月額）→ paid_system_1..5
// 保存先は user_input.amz_monthly_costs（部分更新。変動費ページの物流費は消さない）。

import React, { useCallback, useEffect, useRef, useState } from "react";
import { ToastViewport, useToast } from "../components/Toast";
import { apiGetJson, apiPutJson } from "../utils/apiClient";

const TABLE_CLASS = "min-w-max text-xs border-collapse";
const STICKY_TH = "sticky left-0 bg-gray-100 z-10 min-w-[220px]";
const TH_CELL = "p-1 border min-w-[92px]";
const TD_CELL = "p-1 border text-center align-top";
const LEFT_TD = "sticky left-0 bg-white z-10 p-1 border text-left min-w-[220px]";
const UI_SELECT_CELL = "border rounded px-1 py-0.5 text-[11px] w-[84px] bg-white";
const UI_INPUT_NUM_CELL = "border rounded px-1 py-0.5 text-[11px] w-[84px] text-right";
const UI_INPUT_TEXT = "border rounded px-2 py-1 text-xs w-56";
const UI_SUB_AMOUNT = "text-[10px] text-gray-500 mt-0.5 text-right";

// Amazon出品プラン（税込）
const AMZ_PLANS = [
    { value: "", label: "未設定 / なし", fee: 0 },
    { value: "大口出品", label: "大口出品", fee: 5390 },
    { value: "小口出品", label: "小口出品（月額なし）", fee: 0 },
];

const PAID_ROWS = [
    { costKey: "paid_system_1_cost", label: "有料システム①", nameKey: "paid_system_1_name" },
    { costKey: "paid_system_2_cost", label: "有料システム②", nameKey: "paid_system_2_name" },
    { costKey: "paid_system_3_cost", label: "有料システム③", nameKey: "paid_system_3_name" },
    { costKey: "paid_system_4_cost", label: "有料システム④", nameKey: "paid_system_4_name" },
    { costKey: "paid_system_5_cost", label: "有料システム⑤", nameKey: "paid_system_5_name" },
] as const;

type MonthForm = Record<string, null | number | string>;

function buildMonthKeys(year: number): string[] {
    return Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, "0")}`);
}

export default function AmazonFixedCostPage() {
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState(currentYear);
    const [dataByMonth, setDataByMonth] = useState<Record<string, MonthForm>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [dirtyHintDismissed, setDirtyHintDismissed] = useState(false);
    useEffect(() => {
        if (dirty) setDirtyHintDismissed(false);
    }, [dirty]);

    /** ✅ 未保存ガード：タブ閉じ/リロード時にブラウザ標準の確認を出す（楽天版と同じ） */
    useEffect(() => {
        if (!dirty) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [dirty]);

    /** ✅ 未保存ガード：年切替・ページ切替の前に確認（楽天版と同じ） */
    const confirmDiscardIfDirty = () => {
        if (!dirty) return true;
        return window.confirm("未保存の変更があります。\n移動すると入力内容は破棄されます。よろしいですか？");
    };
    const [error, setError] = useState<string | null>(null);
    const { toasts, pushToast, removeToast } = useToast();
    const loadTokenRef = useRef(0);

    const monthKeys = buildMonthKeys(year);

    const load = useCallback(async () => {
        const token = ++loadTokenRef.current;
        setLoading(true);
        setError(null);
        try {
            const data = await apiGetJson<{
                months: string[];
                rows: Record<string, MonthForm>;
            }>(`/api/amazon/expenses?year_month=${year}-01-01`);
            if (token !== loadTokenRef.current) return;
            setDataByMonth(data.rows ?? {});
            setDirty(false);
        } catch (e) {
            if (token === loadTokenRef.current) setError("固定費の読み込みに失敗しました。");
        } finally {
            if (token === loadTokenRef.current) setLoading(false);
        }
    }, [year]);

    useEffect(() => {
        void load();
    }, [load]);

    const updateMonth = (ym: string, patch: MonthForm) => {
        setDataByMonth((prev) => ({ ...prev, [ym]: { ...prev[ym], ...patch } }));
        setDirty(true);
    };

    const setPlanMonth = (ym: string, planValue: string) => {
        const plan = AMZ_PLANS.find((p) => p.value === planValue);
        updateMonth(ym, { shop_fee: plan ? plan.fee : 0, shop_plan: planValue || null });
    };

    // 名称は全月共通（楽天の自由入力と同じ扱い）
    const setPaidName = (nameKey: string, value: string) => {
        setDataByMonth((prev) => {
            const next: Record<string, MonthForm> = {};
            for (const ym of monthKeys) {
                next[ym] = { ...prev[ym], [nameKey]: value };
            }
            return { ...prev, ...next };
        });
        setDirty(true);
    };

    const handleIntChange = (ym: string, key: string, raw: string) => {
        if (raw === "") {
            updateMonth(ym, { [key]: null });
            return;
        }
        const num = Number(raw);
        if (!Number.isFinite(num) || num < 0) return;
        updateMonth(ym, { [key]: Math.trunc(num) });
    };

    const totalOf = (ym: string): number => {
        const row = dataByMonth[ym] ?? {};
        let total = Number(row.shop_fee) || 0;
        for (const r of PAID_ROWS) total += Number(row[r.costKey]) || 0;
        return total;
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const items = monthKeys.map((ym) => {
                const row = dataByMonth[ym] ?? {};
                const item: Record<string, null | number | string> = { year_month: ym };
                item.shop_plan = row.shop_plan ?? null;
                item.shop_fee = row.shop_fee ?? null;
                for (const r of PAID_ROWS) {
                    item[r.nameKey] = row[r.nameKey] ?? null;
                    item[r.costKey] = row[r.costKey] ?? null;
                }
                return item;
            });
            await apiPutJson("/api/amazon/expenses", { items });
            pushToast("success", "12ヶ月分を保存しました");
            await load();
        } catch (e) {
            setError("保存に失敗しました。もう一度お試しください。");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-3">
            <ToastViewport toasts={toasts} onClose={removeToast}>
                {/* 未保存ヒント（閉じるまで常駐・楽天版と同じ） */}
                {dirty && !dirtyHintDismissed && !saving && (
                    <div className="pointer-events-auto">
                        <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 shadow">
                            <div className="text-xs text-amber-900">変更があります。右上の「12ヶ月分を保存」を押してください。</div>
                            <button
                                className="ml-2 text-xs px-2 py-1 rounded border bg-white hover:bg-gray-50"
                                onClick={() => setDirtyHintDismissed(true)}
                                aria-label="閉じる"
                            >
                                ×
                            </button>
                        </div>
                    </div>
                )}
            </ToastViewport>

            <div className="flex justify-between items-center flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-lg font-semibold">固定費（Amazon / 12ヶ月）</div>
                    <label className="text-xs text-gray-600 ml-2">年</label>
                    <select
                        className="border px-2 py-1 rounded bg-white text-sm h-8"
                        value={year}
                        onChange={(e) => {
                            if (!confirmDiscardIfDirty()) return;
                            setYear(Number(e.target.value));
                        }}
                    >
                        {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                            <option key={y} value={y}>
                                {y}年
                            </option>
                        ))}
                    </select>
                    <button
                        className="px-2 py-1 rounded border bg-white hover:bg-gray-50 text-xs"
                        onClick={() => {
                            if (!confirmDiscardIfDirty()) return;
                            setYear((p) => p - 1);
                        }}
                    >
                        ← 前年
                    </button>
                    <button
                        className="px-2 py-1 rounded border bg-white hover:bg-gray-50 text-xs"
                        onClick={() => {
                            if (!confirmDiscardIfDirty()) return;
                            setYear((p) => p + 1);
                        }}
                    >
                        翌年 →
                    </button>
                </div>
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || loading || !dirty}
                    className={`px-3 py-1 rounded text-xs text-white ${
                        saving || loading || !dirty ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"
                    }`}
                >
                    {saving ? "保存中..." : "12ヶ月分を保存"}
                </button>
            </div>

            {error && (
                <p className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">{error}</p>
            )}

            <div className="overflow-x-auto border rounded relative">
                <table className={TABLE_CLASS}>
                    <thead className="bg-gray-100">
                        <tr>
                            <th className={`text-left p-1 border ${STICKY_TH}`}>科目</th>
                            {monthKeys.map((ym) => (
                                <th key={ym} className={`text-center ${TH_CELL}`}>
                                    {ym.replace("-", "/")}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {/* Amazon出品プラン */}
                        <tr>
                            <td className={LEFT_TD}>
                                <div className="font-medium">Amazon出品プラン</div>
                                <div className="text-[10px] text-gray-500">大口出品: 5,390円（税込）/ 小口出品: 月額なし</div>
                            </td>
                            {monthKeys.map((ym) => (
                                <td key={ym} className={TD_CELL}>
                                    <select
                                        className={UI_SELECT_CELL}
                                        value={String(dataByMonth[ym]?.shop_plan ?? "")}
                                        onChange={(e) => setPlanMonth(ym, e.target.value)}
                                    >
                                        {AMZ_PLANS.map((p) => (
                                            <option key={p.value} value={p.value}>
                                                {p.label}
                                            </option>
                                        ))}
                                    </select>
                                    <div className={UI_SUB_AMOUNT}>
                                        {(Number(dataByMonth[ym]?.shop_fee) || 0).toLocaleString()} 円
                                    </div>
                                </td>
                            ))}
                        </tr>

                        {/* 有料システム①〜⑤（自由入力） */}
                        {PAID_ROWS.map((r) => (
                            <tr key={r.costKey}>
                                <td className={LEFT_TD}>
                                    <div className="flex items-center gap-2">
                                        <div className="font-medium whitespace-nowrap">{r.label}</div>
                                        <input
                                            type="text"
                                            className={UI_INPUT_TEXT}
                                            placeholder="例: 在庫管理ツール、価格改定ツール"
                                            value={String(dataByMonth[monthKeys[0]]?.[r.nameKey] ?? "")}
                                            onChange={(e) => setPaidName(r.nameKey, e.target.value)}
                                        />
                                    </div>
                                </td>
                                {monthKeys.map((ym) => (
                                    <td key={ym} className={TD_CELL}>
                                        <input
                                            type="number"
                                            className={UI_INPUT_NUM_CELL}
                                            value={dataByMonth[ym]?.[r.costKey] ?? ""}
                                            onChange={(e) => handleIntChange(ym, r.costKey, e.target.value)}
                                        />
                                    </td>
                                ))}
                            </tr>
                        ))}

                        {/* 合計 */}
                        <tr className="bg-gray-50">
                            <td className={`${LEFT_TD} bg-gray-50 font-bold`}>合計（固定費合計）</td>
                            {monthKeys.map((ym) => (
                                <td key={ym} className={`${TD_CELL} text-right font-bold bg-gray-50`}>
                                    {totalOf(ym).toLocaleString()} 円
                                </td>
                            ))}
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="text-xs text-gray-500">
                ※固定費は日割りで日次利益に反映されます。毎月1日に前月の設定が自動で引き継がれます。
                <br />
                ※有料システムの登録例：在庫管理・価格改定などの外部有料ツール、人件費、家賃、販管費等
            </div>
        </div>
    );
}
